// Small formatting helpers for Creative Studio (mirrors promotions/campaignUtils).

export function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export function fmtRelative(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function fmtCost(amount, currency = "USD") {
  if (amount == null) return "—";
  const n = Number(amount);
  return `${currency === "USD" ? "$" : ""}${n.toFixed(n < 0.01 ? 4 : 2)}`;
}

export const ASPECT_RATIOS = ["1:1", "3:2", "2:3", "3:4", "4:3", "4:5", "5:4", "9:16", "16:9", "21:9"];

export const STYLE_PRESETS = [
  "photographic", "cinematic", "3d render", "digital art", "watercolor",
  "flat illustration", "minimalist", "product studio", "neon", "vintage",
];

// ---- Visual Prompt Builder (SPRINT-105.7) ---------------------------------
// Grouped, collapsible section config. Each field is provider-agnostic and can
// later expose AI-assist actions (Suggest/Improve/…) without a redesign — the
// `ai` list is a prepared hook (not wired yet). `opts` powers autocomplete.
export const AI_TEXT_HINT = ["Improve", "Expand", "Shorten", "Translate"];  // reserved for AI-assist wiring
export const PROMPT_GROUPS = [
  {
    id: "subject", label: "Subject & Scene", defaultOpen: true,
    fields: [
      { key: "subject", label: "Subject", type: "textarea", placeholder: "A matte-black water bottle", ai: ["Improve", "Expand"] },
      { key: "action", label: "Action", placeholder: "resting, splashing, in motion", ai: ["Suggest"] },
      { key: "characters", label: "Characters", placeholder: "a young athlete, a robot" },
      { key: "environment", label: "Environment", placeholder: "marble kitchen counter", opts: ["studio", "city street", "forest", "beach", "desert", "office", "marble table"], ai: ["Suggest"] },
      { key: "objects", label: "Objects", placeholder: "props, foreground items" },
      { key: "weather", label: "Weather", opts: ["clear", "rain", "fog", "snow", "overcast", "storm"] },
      { key: "time_of_day", label: "Time of Day", opts: ["sunrise", "golden hour", "midday", "blue hour", "night"] },
    ],
  },
  {
    id: "style", label: "Look & Style", defaultOpen: true,
    fields: [
      { key: "style", label: "Style", opts: STYLE_PRESETS, ai: ["Suggest", "Make More Cinematic", "Make More Realistic"] },
      { key: "mood", label: "Mood", opts: ["calm", "energetic", "dramatic", "playful", "luxurious", "moody"] },
      { key: "color_palette", label: "Color Palette", placeholder: "warm earth tones", opts: ["warm earth tones", "cool blues", "pastel", "monochrome", "vibrant", "muted"] },
      { key: "materials", label: "Materials", placeholder: "brushed metal, glass, wood" },
      { key: "lighting", label: "Lighting", opts: ["golden hour", "studio softbox", "rim light", "neon", "natural", "high-key", "low-key"], ai: ["Suggest"] },
    ],
  },
  {
    id: "camera", label: "Camera & Composition",
    fields: [
      { key: "camera", label: "Camera / Shot", opts: ["wide shot", "close-up", "macro", "aerial", "portrait", "over-the-shoulder"], ai: ["Suggest"] },
      { key: "lens", label: "Lens", opts: ["35mm", "50mm", "85mm", "macro", "anamorphic", "fisheye"] },
      { key: "composition", label: "Composition", opts: ["rule of thirds", "centered", "symmetrical", "leading lines", "negative space"] },
    ],
  },
  {
    id: "motion", label: "Motion & Timing", kinds: ["video"],
    fields: [
      { key: "camera_motion", label: "Camera Motion", opts: ["slow dolly in", "orbit", "pan left", "tilt up", "handheld", "crane up"] },
      { key: "duration_seconds", label: "Duration (s)", type: "number", placeholder: "8" },
      { key: "fps", label: "FPS", type: "number", placeholder: "24" },
    ],
  },
  {
    id: "audio", label: "Audio & Voice", kinds: ["video", "audio", "voice"],
    fields: [
      { key: "audio", label: "Audio", placeholder: "ambient city hum" },
      { key: "voice", label: "Voice", placeholder: "warm female narrator" },
      { key: "music", label: "Music", placeholder: "upbeat lo-fi" },
      { key: "dialogue", label: "Dialogue", type: "textarea", placeholder: "\"Taste the difference.\"" },
      { key: "sound_effects", label: "Sound Effects", placeholder: "whoosh, splash" },
      { key: "tone", label: "Tone", opts: ["friendly", "authoritative", "excited", "calm"] },
    ],
  },
  {
    id: "output", label: "Output & Technical",
    fields: [
      { key: "aspect_ratio", label: "Aspect Ratio", type: "select", opts: ASPECT_RATIOS },
      { key: "resolution", label: "Resolution", type: "select", opts: ["", "720p", "1080p", "2k", "4k"] },
      { key: "quality", label: "Quality", type: "select", opts: ["", "draft", "standard", "high"] },
      { key: "fps", label: "FPS", type: "number", kinds: ["video"], placeholder: "24" },
    ],
  },
  {
    id: "advanced", label: "Advanced Settings",
    fields: [
      { key: "negative_prompt", label: "Negative Prompt", type: "textarea", placeholder: "blurry, text, watermark" },
      { key: "seed", label: "Seed", type: "number", placeholder: "random" },
      { key: "language", label: "Language", type: "select", opts: ["en", "es", "fr", "de", "hi", "ja", "pt"] },
    ],
  },
];

// Client-side live preview — mirrors the backend canonical renderer
// (prompt_builder.StructuredPrompt.render). Backend re-renders authoritatively
// at generate time, so this is preview-only.
export function renderStructured(sp = {}, kind = "image") {
  const manual = sp?.extra?.manual_prompt;
  if (manual && String(manual).trim()) return String(manual).trim();
  const seg = [];
  const head = [sp.subject, sp.action, sp.characters].filter(Boolean).join(" ").trim();
  if (head) seg.push(head);
  const scene = [["Environment", sp.environment], ["Objects", sp.objects], ["Weather", sp.weather], ["Time of day", sp.time_of_day]];
  const look = [["Style", sp.style], ["Mood", sp.mood], ["Color palette", sp.color_palette], ["Materials", sp.materials], ["Lighting", sp.lighting]];
  const visual = [["Camera", sp.camera], ["Lens", sp.lens], ["Composition", sp.composition]];
  const motion = [["Camera motion", sp.camera_motion || sp.movement]];
  const audio = [["Audio", sp.audio], ["Voice", sp.voice], ["Music", sp.music], ["Dialogue", sp.dialogue], ["Sound effects", sp.sound_effects], ["Tone", sp.tone]];
  const pick = { image: [...scene, ...look, ...visual], video: [...scene, ...look, ...visual, ...motion, ...audio], audio: [...look, ...audio], voice: audio, text: [["Tone", sp.tone], ["Style", sp.style]] }[kind] || [...scene, ...look, ...visual];
  pick.forEach(([label, val]) => { if (val) seg.push(`${label}: ${val}.`); });
  if (sp.aspect_ratio && ["image", "video"].includes(kind)) seg.push(`Aspect ratio ${sp.aspect_ratio}.`);
  if (sp.resolution && ["image", "video"].includes(kind)) seg.push(`Resolution ${sp.resolution}.`);
  if (sp.duration_seconds && kind === "video") seg.push(`Duration ~${sp.duration_seconds}s.`);
  if (sp.fps && kind === "video") seg.push(`${sp.fps}fps.`);
  if (sp.quality) seg.push(`Quality: ${sp.quality}.`);
  if (sp.language && sp.language !== "en") seg.push(`Language: ${sp.language}.`);
  return seg.filter(Boolean).join(" ").trim();
}

export const TEMPLATE_USE_CASES = [
  { value: "instagram_product", label: "Instagram Product", platform: "instagram", aspect: "1:1" },
  { value: "instagram_reel", label: "Instagram Reel", platform: "instagram", aspect: "9:16" },
  { value: "facebook_banner", label: "Facebook Banner", platform: "facebook", aspect: "16:9" },
  { value: "youtube_thumbnail", label: "YouTube Thumbnail", platform: "youtube", aspect: "16:9" },
  { value: "pinterest_pin", label: "Pinterest Pin", platform: "pinterest", aspect: "2:3" },
  { value: "linkedin_cover", label: "LinkedIn Cover", platform: "linkedin", aspect: "4:1" },
  { value: "marketplace_listing", label: "Marketplace Listing", platform: "marketplace", aspect: "1:1" },
  { value: "email_hero", label: "Email Hero", platform: "email", aspect: "16:9" },
  { value: "landing_page_hero", label: "Landing Page Hero", platform: "web", aspect: "16:9" },
];
